import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import Fab from '@material-ui/core/Fab';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import React from 'react';
import { withRouter } from 'react-router-dom';
import BasicBackground from '../BasicBackground';
import BasicReadField from '../BasicReadField';
import Errored from '../Errored';
import RequestSentIcon from '../icons/RequestSentIcon';
import { authenticationService } from '../services/security/authenticationService';
import { globalStyles } from '../style/gstheme';
import { MAX_PASSWORD_LENGTH } from '../utils/constants';
import { makeCancelable, processHashLocation, validatePassword } from '../utils/utils';
import AccessHeader from './AccessHeader';

const styles = theme => {
    return Object.assign({
        reset: {
            textAlign: 'center',
            height: '560px',
            position: 'fixed',
            top: 'calc(50% - 280px)',
            left: 'calc(50% - 192px)',
            width: 384,
            padding: 20,
            borderRadius: 5,
            backgroundColor: 'white',
        }
    }, globalStyles);
};

class ResetPassword extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            password: "",
            username: "",
            token: null,
            isErrored: null,
            processing: false,
            completed: false
        };
        this.cancellable = null;
        this.textInput = React.createRef();
    }

    componentDidMount() {
        const paramsMap = processHashLocation(this.props.location);
        const username = paramsMap.get("username");
        const token = paramsMap.get("token");
        if (!username || !token) {
            // If no username or token just redirect to login immediately - indicates manual attemp at hacking the URL
            this.props.history.push("/");
        }
        else this.setState({ username, token });
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    submit = async () => {
        this.setState({ processing: true }, async () => {
            try {
                const { password, token } = this.state;
                await this.doReset(password, token).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({ processing: false, completed: true });
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let message = ["I'm afraid we can't process the reset for some reason.", "You may have waited too long to try."];
                this.setState({
                    isErrored: { message },
                    processing: false
                });
            }
        });
    };

    doReset(password, token) {
        this.cancellable = makeCancelable(authenticationService.resetPassword(password, token));
        return this.cancellable;
    }

    clearError = () => {
        this.setState({ isErrored: null }, () => this.props.history.push("/"));
    };

    handleChange = (event) => {
        const t = event.target;
        this.setState({ [t.name]: t.value });
    };

    render() {

        const { username, isErrored, password, processing, completed } = this.state;
        const { classes } = this.props;
        const validPassword = validatePassword(password);
        const disabled = !validPassword || processing;

        return (
            <BasicBackground>
                <Grid container spacing={0} direction="row" className={classes.reset} >
                    <Grid item xs={12}>
                        <AccessHeader title="Reset Password" />
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.loginEntry}>
                            <Typography className={classes.loginSubHeading}>Username</Typography>
                            <BasicReadField margin="dense" fullWidth text={username} locked />
                            <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Password</Typography>
                            <TextField className={classes.loginText}
                                margin="dense"
                                id="password"
                                disabled={completed}
                                name="password"
                                type="password"
                                placeholder="Password"
                                error={password.length > 0 && !validPassword}
                                value={password}
                                variant="outlined"
                                helperText={!completed ? "Min 8 characters including lower case, upper case, a number and a special character from !@#$%^&*" : ""}
                                fullWidth
                                onChange={this.handleChange}
                                onKeyPress={(ev) => {
                                    if (!disabled && ev.charCode === 13) {
                                        this.signup();
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        root: classes.loginInput
                                    },
                                    inputProps: {
                                        maxLength: MAX_PASSWORD_LENGTH
                                    }
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {completed &&
                            <div>
                                <div style={{ marginBottom: '12px' }}>
                                    <RequestSentIcon style={{ width: '36px', height: '32px' }} />
                                </div>
                                <Typography paragraph variant="h3" className={classes.mainHeadingSecondary}>Reset Sent</Typography>
                            </div>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        {completed ?
                            <Typography variant="body2">Please check your email.</Typography>
                            :
                            <Fab
                                disabled={disabled}
                                variant="extended"
                                color="secondary"
                                className={classes.button}
                                style={{ width: '120px' }}
                                onClick={() => this.submit()}>Submit</Fab>
                        }
                    </Grid>
                </Grid>
                {isErrored && <Errored data={isErrored} clearError={this.clearError} />}
            </BasicBackground>
        );
    }
}

ResetPassword.propTypes = {
    classes: PropTypes.object,
    history: PropTypes.object
};

export default withRouter(withStyles(styles)(ResetPassword));